import React, { useState, useEffect, useCallback } from 'react';
import { battlePersonApi } from '../../api';
import HrnbCard from '../controls/HrnbCard';
import HrnbButton from '../controls/HrnbButton';
import HrnbInput from '../controls/HrnbInput';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbSelect from '../controls/HrnbSelect';
import { getFormatIcon } from '../../utils/battleFormats';
import { SaveIcon, RejectIcon } from '../../icons';

const getStatusLabel = (status) => {
    const labels = {
        PENDING: 'Ожидает одобрения',
        APPROVED: 'Одобрен',
        REJECTED: 'Отклонён',
        WITHDRAWN: 'Отозван',
    };
    return labels[status] || status;
};

const getRoleLabel = (role) => {
    const labels = { ORGANIZER: 'Организатор', PARTICIPANT: 'Участник', JUDGE: 'Жюри' };
    return labels[role] || role;
};

const FORMAT_OPTIONS = [
    { value: '', label: 'Все форматы' },
    { value: 'AUDIO', label: '🎵 Аудио' },
    { value: 'TEXT', label: '📝 Текст' },
];

export default function MyBattlePersons({ publicProfileId }) {
    const [battlePersons, setBattlePersons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState({});
    const [saving, setSaving] = useState({});
    const [filterFormat, setFilterFormat] = useState('');

    const fetchMyBattlePersons = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // GET /api/battle_person — возвращает участия текущего пользователя
            const data = await battlePersonApi.list({ size: 100 });
            const all = data?.content || [];
            // Скрываем отозванные заявки
            setBattlePersons(all.filter(bp => bp.applicationStatus !== 'WITHDRAWN'));
        } catch (err) {
            setError('Не удалось загрузить ваши участия в баттлах');
            console.error('[MyBattlePersons] Failed:', err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (publicProfileId) {
            fetchMyBattlePersons();
        } else {
            setLoading(false);
        }
    }, [publicProfileId, fetchMyBattlePersons]);

    const handleEditChange = (id, field, value) => {
        setEditing(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value }
        }));
    };

    const handleSave = async (id) => {
        const changes = editing[id];
        if (!changes) return;
        setSaving(prev => ({ ...prev, [id]: true }));
        try {
            await battlePersonApi.patch(id, changes);
            await fetchMyBattlePersons();
            setEditing(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
        } catch (err) {
            setError('Не удалось сохранить изменения');
            console.error('[MyBattlePersons] Save failed:', err.message);
        } finally {
            setSaving(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleWithdraw = async (id) => {
        if (!window.confirm('Вы уверены, что хотите отозвать заявку?')) return;
        try {
            await battlePersonApi.withdraw(id);
            await fetchMyBattlePersons();
        } catch (err) {
            setError('Не удалось отозвать заявку');
            console.error('[MyBattlePersons] Withdraw failed:', err.message);
        }
    };

    if (loading) return <HrnbLoader />;
    if (!publicProfileId) return <p className="text-muted">Создайте публичный профиль, чтобы управлять участием в баттлах.</p>;
    if (error) return <HrnbAlert variant="danger" onClose={() => setError(null)}>{error}</HrnbAlert>;

    // Фильтрация по формату
    const filteredBattlePersons = filterFormat
        ? battlePersons.filter(bp => bp.battle?.format === filterFormat)
        : battlePersons;

    if (filteredBattlePersons.length === 0) {
        return <p className="text-muted">Вы пока не участвуете ни в одном баттле{filterFormat ? ' выбранного формата' : ''}.</p>;
    }

    return (
        <div className="my-battle-persons">
            <div className="my-battle-persons-filters" style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
                <HrnbSelect
                    value={filterFormat}
                    onChange={setFilterFormat}
                    options={FORMAT_OPTIONS}
                    placeholder="Фильтр по формату"
                    style={{ maxWidth: 200 }}
                />
                {filterFormat && (
                    <HrnbButton variant="outline" size="sm" onClick={() => setFilterFormat('')}>
                        Сбросить фильтр
                    </HrnbButton>
                )}
            </div>
            {filteredBattlePersons.map((bp) => {
                const editData = editing[bp.id] || {};
                const isEditing = !!editing[bp.id];
                return (
                    <HrnbCard
                        key={bp.id}
                        className="my-battle-person-card"
                        title={`${getRoleLabel(bp.role)} — ${getStatusLabel(bp.applicationStatus)}`}
                    >
                        <div className="my-battle-person-fields">
                            <div className="field-row">
                                <label>Имя:</label>
                                <HrnbInput
                                    value={isEditing ? (editData.name ?? bp.name ?? '') : (bp.name || '')}
                                    onChange={e => handleEditChange(bp.id, 'name', e.target.value)}
                                />
                            </div>
                            <div className="field-row">
                                <label>Nickname:</label>
                                <HrnbInput
                                    value={isEditing ? (editData.nickname ?? bp.nickname ?? '') : (bp.nickname || '')}
                                    onChange={e => handleEditChange(bp.id, 'nickname', e.target.value)}
                                />
                            </div>
                            <div className="field-row">
                                <label>Город:</label>
                                <HrnbInput
                                    value={isEditing ? (editData.city ?? bp.city ?? '') : (bp.city || '')}
                                    onChange={e => handleEditChange(bp.id, 'city', e.target.value)}
                                />
                            </div>
                            {bp.seed !== undefined && bp.seed !== null && (
                                <div className="field-row">
                                    <label>Сид:</label>
                                    <span>{bp.seed}</span>
                                </div>
                            )}
                        </div>
                        <div className="my-battle-person-actions">
                            <HrnbButton
                                variant="primary"
                                size="sm"
                                onClick={() => handleSave(bp.id)}
                                disabled={saving[bp.id] || !isEditing}
                                icon={<SaveIcon/>}
                            >
                                {saving[bp.id] ? 'Сохранение...' : 'Сохранить'}
                            </HrnbButton>
                            {bp.applicationStatus !== 'WITHDRAWN' && (
                                <HrnbButton
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleWithdraw(bp.id)}
                                    icon={<RejectIcon/>}
                                >
                                    Отозвать заявку
                                </HrnbButton>
                            )}
                        </div>
                    </HrnbCard>
                );
            })}
        </div>
    );
}
